import { useCallback, useRef, useState } from "react";
import moment from "moment";
import { calendarService, taskService } from "@miplatform/api";
import {
  ATTEMPT_DERIVED_STATUS,
  BOARD_MODAL_MODE,
  CONFIGURABLE_COLORS,
  EMAIL_TYPE,
  EMAIL_TYPE_TO_EMAIL_TEMPLATE_TYPE_MAP,
  HTTP_STATUSES,
  VIEWS,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  createUUID,
  deepCopy,
  getCurrentTime,
  getDateWithAddedTime,
  getGUIDArray,
  getOrFilter,
  isBeforeNow,
  isEmpty,
  isSameDay,
  isSameTime,
  isWithinLockPermission,
} from "@miplatform/utils";
import { useGlobalManagerApproval } from "../../../components/GlobalManagerApprovalProvider/GlobalManagerApprovalProvider";
import { attachEventTimes } from "../utils";
import { SCHEDULE_VIEWS } from "./scheduleViews";
import { useSchedulerViewModel } from "./useSchedulerViewModel";

const getDateRangeFilter = (range) =>
  getOrFilter(
    {
      scheduledStartDate: range,
      startDate: null,
    },
    {
      startDate: range,
    }
  );

const INITIAL_DATE_FILTER = getDateRangeFilter({
  ge: moment().startOf("day").toDate(),
  le: moment().endOf("day").toDate(),
});

const MODALS = {
  AUTO_LOCK_SCHEDULE: 1,
  AUTO_LOCK_RESCHEDULE: 2,
};

const PAUSE_CAROUSEL_TYPES = {
  DRAGGING: "dragging",
  HOVERING: "hovering",
  DETAIL_DRAWER: "detail_drawer",
  DAYS_VIEW: "days_view",
};

const BOARD_SUB_TEXT = "Please try again later.";

export const useSchedulerController = ({
  viewConfig,
  onRefresh,
  calendarSet,
}) => {
  const [time, setTime] = useState(getCurrentTime());
  const [dateFilterItem, setDateFilterItem] = useState(INITIAL_DATE_FILTER);
  const [config, setConfig] = useState({});
  const [displayData, setDisplayData] = useState([]);
  const [modal, setModal] = useState({});
  const [updateInfo, setUpdateInfo] = useState({});
  const [scheduleView, setScheduleView] = useState(SCHEDULE_VIEWS.CALENDAR);
  const [isDragging, setIsDragging] = useState(false);
  const [hasModalOpen, setHasModalOpen] = useState(false);
  const [isCreateNewOrderDrawerOpen, setIsCreateNewOrderDrawerOpen] =
    useState(false);
  const [isTechnicianDrawerOpen, setIsTechnicianDrawerOpen] = useState(false);
  const [isBoardModalOpen, setIsBoardModalOpen] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [attemptsLoading, setAttemptsLoading] = useState(new Set());
  const [emailModal, setEmailModal] = useState(null);
  const [emailDrawersMap, setEmailDrawersMap] = useState({});
  const [minimizedStatusMap, setMinimizedStatusMap] = useState({});
  const [searchedAttemptId, setSearchedAttemptId] = useState("");
  const currentLoadAppointmentReq = useRef(null);
  const stateManager = useComponentState();

  const createBoard = useCallback(
    (data) => {
      stateManager.run(async () => {
        const response = await calendarService.boards.create({
          data: {
            ...data,
            color:
              data?.color ||
              CONFIGURABLE_COLORS.find(
                ({ color }) =>
                  !viewConfig.boards.some((board) => board.color === color)
              )?.color ||
              CONFIGURABLE_COLORS[0].color,
            calendarId: viewConfig?.id,
          },
          toastMessageConfig: {
            success: {
              text: "Board created successfully",
            },
            failure: {
              text: "Failed to create board",
              subtext: BOARD_SUB_TEXT,
            },
          },
        });

        if (response.status === HTTP_STATUSES.CREATED) {
          onRefresh();
        }
      });
    },
    [viewConfig]
  );

  const updateBoard = useCallback(
    (data) => {
      stateManager.run(async () => {
        const response = await calendarService.boards.update({
          id: data.id,
          data: {
            ...data,
            calendarId: viewConfig?.id,
          },
          toastMessageConfig: {
            success: {
              text: "Board updated successfully",
            },
            failure: {
              text: "Failed to update board",
              subtext: BOARD_SUB_TEXT,
            },
          },
        });

        if (response.status === HTTP_STATUSES.OK) {
          onRefresh();
        }
      });
    },
    [viewConfig]
  );

  const deleteBoard = useCallback((data) => {
    stateManager.run(async () => {
      const response = await calendarService.boards.delete({
        id: data.id,
        modifiedOn: data.modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Board deleted successfully",
          },
          failure: {
            text: "Failed to delete board",
            subtext: BOARD_SUB_TEXT,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        onRefresh();
      }
    });
  }, []);

  const updateBoardConfig = useCallback(
    (data, mode) => {
      switch (mode) {
        case BOARD_MODAL_MODE.CREATE:
          createBoard(data);
          break;
        case BOARD_MODAL_MODE.UPDATE:
          updateBoard(data);
          break;
        case BOARD_MODAL_MODE.DELETE:
          deleteBoard(data);
          break;
        default:
          break;
      }
    },
    [createBoard, deleteBoard, updateBoard]
  );

  const updateUsersOnBoard = async (boardId, users) => {
    stateManager.run(async () => {
      const response = await calendarService.boards.update({
        id: boardId,
        data: {
          modifiedOn: viewConfig.boards.find((board) => board.id === boardId)
            ?.modifiedOn,
          users,
        },
        toastMessageConfig: {
          success: {
            text: "Technician View Saved",
            subtext:
              "Your technician view settings have been saved successfully. Your customized list will now appear on your dispatch board.",
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        onRefresh();
      }
    });
  };

  const handleEmailModalClose = useCallback(() => {
    setEmailModal(null);
  }, []);

  const handleEmailModalOpen = useCallback(({ attempt, type }) => {
    setEmailModal({ attempt, type });
  }, []);

  const handleAttemptLoading = (attemptId, isLoading) => {
    setAttemptsLoading((prev) => {
      const next = new Set(prev);
      if (isLoading) {
        next.add(attemptId);
      } else {
        next.delete(attemptId);
      }
      return next;
    });
  };

  const { executeApiRequest } = useGlobalManagerApproval();

  const schedule = async ({
    attemptId,
    data,
    lock = false,
    orderModifiedOn,
    callback = () => null,
    abortCallback = () => null,
  }) => {
    const options = await taskService.attempts.getScheduleOptions({
      attemptId,
      data: { ...data, calendarConfigId: viewConfig.id },
      lock,
      orderModifiedOn,
    });

    const response = await executeApiRequest({
      options,
      toastMessageForManagerApproval: {
        text: "Order Cannot Be Scheduled",
        subtext: "Manager Approval Required.",
      },
      callback,
      abortCallback,
    });

    if (response.status === HTTP_STATUSES.OK) {
      return response.data;
    }
    if (response.status === HTTP_STATUSES.REDIRECT) {
      return { autoLock: true };
    }
    if (response.error) {
      return { error: response.errorBody };
    }

    return null;
  };

  const reschedule = async ({
    attemptId,
    data,
    lock = false,
    isLock = false,
    callback = () => null,
    abortCallback = () => null,
  }) => {
    const options = await taskService.attempts.getRescheduleOptions({
      attemptId,
      data: { ...data, calendarConfigId: viewConfig.id },
      lock,
    });
    const schedulingRuleError = {
      text: "Order Cannot Be Scheduled",
      subtext: "Manager Approval Required.",
    };

    const managerApprovalLockError = {
      text: "Order is locked. Manager approval needed.",
      subtext:
        "The order is locked and needs manager authorization before it can be moved.",
    };

    await executeApiRequest({
      options,
      toastMessageForManagerApproval: isLock
        ? managerApprovalLockError
        : schedulingRuleError,
      callback,
      abortCallback,
    });
  };

  const handleBoardModalClose = useCallback(() => {
    setIsBoardModalOpen(null);
  }, []);

  const handleBoardModalOpen = useCallback((type) => {
    setIsBoardModalOpen(type);
  }, []);

  const handleTechnicianDrawerClose = useCallback(() => {
    setIsTechnicianDrawerOpen(false);
  }, []);

  const appendOrderTypeColor = useCallback((data) => {
    return data.map((e) => ({
      ...e,
      assignment: {
        ...e.assignment,
        assignmentTemplateColor: e.assignment?.order?.orderTemplateColor,
      },
    }));
  }, []);

  const {
    data,
    users,
    selectedTechnician,
    calendarView,
    date,
    dateRange,
    isSignalRConnected,
    selectedBoard,
    boardUsers,
    buckets,
    carouselMode,
    carouselInterval,
    isCarouselPaused,
    closeoutDrawersMap,
    setCloseoutDrawersMap,
    pauseCarouselMode,
    resumeCarouselMode,
    onChangeNextBoard,
    onChangePrevBoard,
    setCarouselInterval,
    toggleCarouselMode,
    setSelectedBoard,
    setBuckets,
    loadBuckets,
    setCalendarView,
    setSelectedTechnician,
    setData,
    setDate,
    setDateRange,
    startConnectionAndSubscribe,
    stopAndRemoveHub,
    getAttempts,
    getDataForDisplay,
    loadUsers,
    checkConflicts,
    attachEventTime,
    updateAttempt,
    updateCloseoutDrawersMapOnAttemptUpdated,
  } = useSchedulerViewModel({ viewConfig, calendarSet });

  const orderTemplateIds =
    viewConfig?.workBaseTemplates?.map((template) => template.id) ?? [];

  const updateTime = () => {
    const updatedTime = getCurrentTime();

    if (updatedTime.hour !== time.hour) {
      setTime(updatedTime);
    }
  };

  const loadSchedulerConfig = () => {
    if (!viewConfig) {
      return;
    }
    loadAppointments(calendarView);
    loadBuckets(viewConfig);
    loadUsers(viewConfig);
    setConfig({
      increment: viewConfig.timelineInterval,
      groupBy: "assignedUserId",
    });
  };

  const onDateChange = (newStartDate, newEndDate) => {
    setDateFilterItem(
      getDateRangeFilter({
        ge: moment(newStartDate).startOf("day").toDate(),
        le: moment(newEndDate || newStartDate)
          .endOf("day")
          .toDate(),
      })
    );

    setDateRange({
      startDate: newStartDate,
      endDate: moment(getDateWithAddedTime(newStartDate, { d: 6 })),
    });
  };

  const loadAppointments = (view) => {
    if (!dateFilterItem) {
      return;
    }
    const uuid = createUUID();
    currentLoadAppointmentReq.current = uuid;
    stateManager.run(async () => {
      const filterItem =
        view === VIEWS.DAY
          ? dateFilterItem
          : {
              assignedUserId: {
                type: "guid",
                value: selectedTechnician.id,
              },
              ...getDateRangeFilter({
                ge: moment(dateRange.startDate).startOf("day").toDate(),
                le: moment(dateRange.endDate).endOf("day").toDate(),
              }),
            };

      const { data: result, status } = await getAttempts({
        filters: {
          ...filterItem,
          ...viewConfig.filter,
          ...(orderTemplateIds.length > 0 && {
            assignment: {
              [calendarSet.eventType || "order"]: {
                [`${calendarSet.eventType || "order"}TemplateId`]: {
                  in: getGUIDArray(viewConfig.orderTemplateIds),
                },
              },
            },
          }),
        },
      });

      if (
        status === HTTP_STATUSES.OK &&
        uuid === currentLoadAppointmentReq.current
      ) {
        const finalResult = attachEventTimes(result);
        setData(finalResult);
      }
    });
  };

  const onEventUpdate = (event, updates, params = {}) => {
    if (
      isSameTime(event.scheduledEndDate, updates.scheduledEndDate) &&
      event.assignedUserId === updates.userId
    ) {
      return;
    }

    const isAdjusting =
      isSameTime(event.scheduledStartDate, updates.scheduledStartDate) &&
      event.assignedUserId === updates.userId;

    const updatedEvent = {
      ...event,
      ...updates,
      delayedTime: isAdjusting ? event.delayedTime : 0,
      assignedUserId: updates.userId,
    };

    const resultedData = displayData.map((e) =>
      e.id === event.id ? attachEventTime(updatedEvent) : e
    );

    const targetColumn = resultedData.filter(
      (e) =>
        e.assignedUserId === updates.userId &&
        isSameDay(e.displayStartTime, updates.scheduledStartDate) &&
        !e.isProposing &&
        !e.isCapacityTracker
    );

    if (checkConflicts(targetColumn, updatedEvent)) {
      toastService.error({ text: "Conflicts exist in the target column." });
      return;
    }

    if (isBeforeNow(updates.scheduledStartDate) && !isAdjusting) {
      toastService.error({ text: "Please schedule to a future time." });
      return;
    }

    setDisplayData(resultedData);
    handleAttemptLoading(event.id, true);
    reschedule({
      id: event.id,
      attemptId: event.id,
      assignmentTemplateAllowableScheduleLimit:
        event.assignment.assignmentTemplateAllowableScheduleLimit,
      data: {
        ...updates,
        modifiedOn: event.modifiedOn,
        reason: params.reason,
        supervisorCode: params.supervisorCode,
      },
      lock: params.lock,
      isLock: !isWithinLockPermission({ event, updates }),
      abortCallback: () => {
        setData(deepCopy(data));
        handleAttemptLoading(event.id, false);
      },
      callback: async (response) => {
        if (response.status === HTTP_STATUSES.OK) {
          toastService.success({
            text: "Attempt Placed",
            subtext: `${event.home.address.addressLine1} was ${
              isAdjusting ? "adjusted" : "rescheduled"
            }.`,
          });
          const attempt = response.data;
          if (
            orderTemplateIds.length > 0 &&
            !viewConfig.orderTemplateIds.includes(attempt.orderTemplateId)
          ) {
            handleAttemptLoading(event.id, false);
            return;
          }
          // Update the other attempts in the same order to have the same order, so that FE still displays
          // correct multi-technician alignment instantly even when signalR is disconnected.
          if (attempt) {
            if (
              !isSameDay(attempt.scheduledStartDate, event.scheduledStartDate)
            ) {
              setEmailModal({ attempt, type: EMAIL_TYPE.SCHEDULE });
            }

            updateCloseoutDrawersMapOnAttemptUpdated(attempt);
            const index = data.findIndex(
              (x) =>
                x.assignment.id === attempt.assignment.id &&
                x.id === attempt.id &&
                x.modifiedOn <= attempt.modifiedOn
            );
            if (index !== -1) {
              data[index] = attempt;
              setData(deepCopy(data));
            }
          }

          setData([
            ...data.filter((item) => item.id !== event.id),
            attachEventTime(attempt),
          ]);
          handleAttemptLoading(event.id, false);
        }

        if (response.status === HTTP_STATUSES.REDIRECT) {
          setModal({ type: MODALS.AUTO_LOCK_RESCHEDULE });
          setUpdateInfo({ event, updates });
        }

        if (!response || response.error) {
          setData(deepCopy(data));
          handleAttemptLoading(event.id, false);
        }
      },
    });
  };

  const onEventCreate = (event, updates, params = {}) => {
    if (isBeforeNow(updates.scheduledStartDate)) {
      toastService.error({ text: "Please schedule to a future time." });
      return;
    }

    if (!updates.userId) {
      updates.userId = selectedTechnician.id;
    }

    const createdEvent = {
      ...event,
      ...updates,
      assignedUserId: updates.userId,
    };

    const resultedData = [
      ...displayData.filter((e) => e.id !== event.id),
      attachEventTime(createdEvent),
    ];

    const targetColumn = resultedData.filter(
      (e) =>
        e.assignedUserId === updates.userId &&
        isSameDay(e.displayStartTime, updates.scheduledStartDate) &&
        !e.isCapacityTracker
    );

    if (checkConflicts(targetColumn, createdEvent)) {
      toastService.error({ text: "Conflicts exist in the target column." });
      return;
    }

    setDisplayData(resultedData);
    handleAttemptLoading(event.id, true);
    schedule({
      attemptId: event.id,
      data: updates,
      lock: params.lock,
      assignmentTemplateAllowableScheduleLimit:
        event.assignment.assignmentTemplateAllowableScheduleLimit,
      orderModifiedOn: event.assignment.order?.modifiedOn,
      abortCallback: () => {
        setData(deepCopy(data));
        handleAttemptLoading(event.id, false);
      },
      callback: (response) => {
        if (response.status === HTTP_STATUSES.OK) {
          toastService.success({
            text: "Attempt Placed",
            subtext: `${event.home.address.addressLine1} was scheduled.`,
          });

          loadBuckets(viewConfig);
          const { data: attempt } = response;
          if (
            orderTemplateIds.length > 0 &&
            !orderTemplateIds.includes(attempt.orderTemplateId)
          ) {
            handleAttemptLoading(event.id, false);
            return;
          }
          // multi-technician alignment when signalR is disconnected. Change Multi technician attempt data
          if (attempt) {
            setEmailModal({ attempt, type: EMAIL_TYPE.SCHEDULE });
            updateCloseoutDrawersMapOnAttemptUpdated(attempt);
            const index = data.findIndex(
              (x) =>
                x.assignment.id === attempt.assignment.id &&
                x.id === attempt.id &&
                x.modifiedOn <= attempt.modifiedOn
            );
            if (index !== -1) {
              data[index] = attempt;
              setData(deepCopy(data));
            }
          }
          // add attempt to the calendar data
          const foundIndex = data.findIndex((item) => item.id === attempt?.id);
          if (foundIndex === -1 && attempt) {
            setData([...data, attachEventTime(attempt)]);
          }
        }

        handleAttemptLoading(event.id, false);

        if (response.status === HTTP_STATUSES.REDIRECT) {
          setModal({ type: MODALS.AUTO_LOCK_SCHEDULE });
          setUpdateInfo({ event, updates });
          return;
        }

        if (!response || response.error) {
          setData(deepCopy(data));
          handleAttemptLoading(event.id, false);
        }
      },
    });
  };

  const onSelectedTechnicianChange = (technician) => {
    setSelectedTechnician(technician);
  };

  const onColumnHeaderClick = (params = {}) => {
    if (calendarView === VIEWS.DAY) {
      setCalendarView(VIEWS.DAYS);
      setSelectedTechnician(params.user);
      pauseCarouselMode(PAUSE_CAROUSEL_TYPES.DAYS_VIEW);
      return;
    }
    if (calendarView === VIEWS.DAYS) {
      setCalendarView(VIEWS.DAY);
      setDate(moment(params.date));
      resumeCarouselMode(PAUSE_CAROUSEL_TYPES.DAYS_VIEW);
    }
  };

  const processData = ({ originalData = data, originalUsers = users } = {}) => {
    setDisplayData(
      appendOrderTypeColor(
        getDataForDisplay({
          currTime: time.hour,
          data:
            calendarView === VIEWS.DAY
              ? originalData.filter((e) => isSameDay(e.displayStartTime, date))
              : originalData,
          users: originalUsers,
          groupBy: config.groupBy,
        })
      )
    );
  };

  const closeModal = () => {
    setModal({});
    setData(deepCopy(data));
  };

  const handleAutoLockAction = () => {
    setModal({});

    const { event, updates } = updateInfo;

    (event.fromBucket ? onEventCreate : onEventUpdate)(event, updates, {
      lock: true,
    });
  };

  const onDrag = (event) => {
    if (event) {
      pauseCarouselMode(PAUSE_CAROUSEL_TYPES.DRAGGING);
      setIsDragging(true);
    } else {
      resumeCarouselMode(PAUSE_CAROUSEL_TYPES.DRAGGING);
      setIsDragging(false);
    }
  };

  const onCardHover = (event) => {
    if (event) {
      pauseCarouselMode(PAUSE_CAROUSEL_TYPES.HOVERING);
    } else {
      resumeCarouselMode(PAUSE_CAROUSEL_TYPES.HOVERING);
    }
  };

  const handleEmailDrawerOpen = useCallback(
    ({ attempt, type }) => {
      if (Object.values(closeoutDrawersMap).some(({ isOpen }) => isOpen)) {
        setCloseoutDrawersMap((prev) =>
          Object.fromEntries(
            Object.entries(prev).filter(
              ([, { isOpen, wasMinimized }]) => !isOpen && wasMinimized
            )
          )
        );
      }

      setEmailDrawersMap((prev) => ({
        ...prev,
        ...EMAIL_TYPE_TO_EMAIL_TEMPLATE_TYPE_MAP[type].reduce(
          (acc, templateType) => ({
            ...acc,
            [templateType]: {
              attempt,
              templateType,
              isOpen: true,
            },
          }),
          {}
        ),
      }));
    },
    [closeoutDrawersMap]
  );

  const handleEmailDrawerClose = useCallback((templateType) => {
    setEmailDrawersMap((prev) => ({
      ...prev,
      [templateType]: { ...prev[templateType], isOpen: false },
    }));

    setTimeout(
      () =>
        setEmailDrawersMap((prev) => {
          const newState = { ...prev };
          delete newState[templateType];
          return newState;
        }),
      300
    );
  }, []);

  const handleOpenCloseoutDrawer = (attempt, { tab, screen, view } = {}) => {
    if (Object.keys(emailDrawersMap).length > 0) {
      setEmailDrawersMap({});
    }

    [
      ATTEMPT_DERIVED_STATUS.PENDING_CLOSEOUT,
      ATTEMPT_DERIVED_STATUS.PENDING_PREBILL,
    ].forEach((statusType) => {
      if (minimizedStatusMap[statusType] === attempt.id) {
        setMinimizedStatusMap((prev) => {
          const newState = { ...prev };
          delete newState[statusType];
          return newState;
        });
      }
    });
    setCloseoutDrawersMap((prev) => {
      const closedDrawers = Object.fromEntries(
        Object.entries(prev).filter(([, { isOpen }]) => !isOpen)
      );
      const wasMinimized = Boolean(prev[attempt.id]);

      return {
        ...closedDrawers,
        [attempt.id]: {
          attempt,
          isOpen: true,
          wasMinimized,
          ...(tab && !wasMinimized && { tab }),
          ...(screen && !wasMinimized && { screen }),
          ...(view && !wasMinimized && { view }),
        },
      };
    });
    pauseCarouselMode(PAUSE_CAROUSEL_TYPES.DETAIL_DRAWER);
  };

  const handleMinimizeCloseoutDrawer = (attemptId, derivedStatus) => {
    setCloseoutDrawersMap((prev) => ({
      ...prev,
      [attemptId]: { ...prev[attemptId], isOpen: false, wasMinimized: true },
    }));
    setMinimizedStatusMap((prev) => ({
      ...prev,
      [derivedStatus]: attemptId,
    }));
    resumeCarouselMode(PAUSE_CAROUSEL_TYPES.DETAIL_DRAWER);
  };

  const handleCloseAllCloseoutDrawers = () => {
    setCloseoutDrawersMap({});
    resumeCarouselMode(PAUSE_CAROUSEL_TYPES.DETAIL_DRAWER);
  };

  const handleCloseCloseoutDrawer = (attemptId) => {
    setCloseoutDrawersMap((prev) => ({
      ...prev,
      [attemptId]: { ...prev[attemptId], isOpen: false, wasMinimized: false },
    }));

    setTimeout(
      () =>
        setCloseoutDrawersMap((prev) => {
          const newState = { ...prev };
          delete newState[attemptId];
          return newState;
        }),
      300
    );

    resumeCarouselMode(PAUSE_CAROUSEL_TYPES.DETAIL_DRAWER);
  };

  const handleCloseCreateNewOrderDrawer = () => {
    setIsCreateNewOrderDrawerOpen(false);
  };

  const onAttemptUpdated = (attempt) => {
    if (attempt) {
      updateAttempt(attempt);
      updateCloseoutDrawersMapOnAttemptUpdated(attempt);
    }
  };

  const onSearchSelect = (attempt) => {
    if (isEmpty(attempt)) {
      setSearchedAttemptId("");
      return;
    }

    if (!attempt.isInBucket) {
      setDate(moment(attempt.scheduledStartDate));
      onDateChange(moment(attempt.scheduledStartDate));
    }

    setSearchedAttemptId(attempt.id);
  };

  const getColorsAppendedBuckets = (buckets) => {
    if (isEmpty(buckets)) {
      return {};
    }
    const modifiedBuckets = {};
    Object.entries(buckets).forEach(([calendarId, calendarBuckets]) => {
      modifiedBuckets[calendarId] = appendOrderTypeColor(
        calendarBuckets?.map((bucket) => ({
          ...bucket,
          data: appendOrderTypeColor(bucket.data),
        }))
      );
    });
    return modifiedBuckets;
  };

  return {
    config,
    users,
    data,
    displayData,
    buckets: getColorsAppendedBuckets(buckets),
    dateFilterItem,
    date,
    dateRange,
    selectedTechnician,
    scheduleView,
    calendarView,
    time,
    modal,
    isDragging,
    MODALS,
    isSignalRConnected,
    initialLoading: stateManager.isBusy(),
    isCreateNewOrderDrawerOpen,
    isTechnicianDrawerOpen,
    selectedBoard,
    boardUsers,
    isBoardModalOpen,
    selectedUserId,
    carouselMode,
    carouselInterval,
    isCarouselPaused,
    emailModal,
    closeoutDrawersMap,
    emailDrawersMap,
    minimizedStatusMap,
    attemptsLoading,
    searchedAttemptId,
    hasModalOpen,
    setHasModalOpen,
    updateUsersOnBoard,
    onSelectedTechnicianChange,
    updateBoardConfig,
    appendOrderTypeColor,
    handleEmailDrawerOpen,
    handleEmailModalOpen,
    handleOpenCloseoutDrawer,
    handleCloseCloseoutDrawer,
    handleCloseAllCloseoutDrawers,
    handleMinimizeCloseoutDrawer,
    handleCloseCreateNewOrderDrawer,
    handleEmailModalClose,
    handleEmailDrawerClose,
    onCardHover,
    onChangePrevBoard,
    onChangeNextBoard,
    toggleCarouselMode,
    setCarouselInterval,
    setSelectedUserId,
    setBuckets,
    handleBoardModalOpen,
    handleBoardModalClose,
    setSelectedBoard,
    setIsTechnicianDrawerOpen,
    handleTechnicianDrawerClose,
    setIsCreateNewOrderDrawerOpen,
    updateTime,
    setDate,
    loadSchedulerConfig,
    startConnectionAndSubscribe,
    stopAndRemoveHub,
    loadAppointments,
    onDateChange,
    onEventUpdate,
    onEventCreate,
    onColumnHeaderClick,
    setScheduleView,
    setCalendarView,
    processData,
    closeModal,
    loadBuckets,
    onDrag,
    handleAutoLockAction,
    onAttemptUpdated,
    onSearchSelect,
    loadUsers,
  };
};
